import { getAuth } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export interface NatureDrSuggestion {
  suggestion: string;
  detailedSuggestion: string;
}

export interface NatureDrResponse {
  suggestions: NatureDrSuggestion[];
  warning: string;
}

export interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  current_period_start: number;
  cancel_at_period_end?: boolean;
  cancel_at?: number;
  plan?: {
    id: string;
    nickname?: string;
    amount?: number;
    interval?: string;
    interval_count?: number;
    currency?: string;
  };
}

export interface SubscriptionResponse {
  subscriptions: Subscription[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

export const getNatureDrResponse = async (
  userMessage: string,
  history: ChatMessage[] = []
): Promise<NatureDrResponse> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_URL}/naturedr`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message: userMessage, history })
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    return {
      suggestions: [
        {
          suggestion: 'Sorry, there was an error contacting our service.',
          detailedSuggestion: 'We encountered a technical issue while attempting to generate a response.'
        }
      ],
      warning: 'Warning: Always consult with a healthcare professional for medical advice.'
    };
  }
};

interface SubscriptionCancelResponse {
  subscription: any;
}

export const cancelSubscription = async (subscriptionId: string): Promise<SubscriptionCancelResponse> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/cancel-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ subscriptionId })
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  return await response.json();
};

export const getSubscriptionInfo = async (): Promise<SubscriptionResponse> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/subscription`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  return await response.json();
};

// ============================================
// Profile API
// ============================================

export interface UserProfile {
  dateOfBirth: string;
  biologicalSex: 'male' | 'female' | 'prefer_not_to_say';
  heightCm: number;
  weightKg: number;
  ethnicity?: string;
  isPregnant?: boolean;
  conditions?: string[];
  medications?: string[];
  onboardingComplete: boolean;
  updatedAt?: string;
}

export interface ProfileResponse {
  profile: UserProfile | null;
}

export const getProfile = async (): Promise<ProfileResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/profile`, { method: 'GET', headers });
  if (!response.ok) throw new Error(`Server responded with status: ${response.status}`);
  return await response.json();
};

export interface SaveProfilePayload {
  dateOfBirth: string;
  biologicalSex: 'male' | 'female' | 'prefer_not_to_say';
  heightUnit: 'cm' | 'feet';
  heightCm?: number;
  heightFeet?: number;
  heightInches?: number;
  weightUnit: 'kg' | 'stone' | 'lbs';
  weightKg?: number;
  weightStone?: number;
  weightLbs?: number;
  ethnicity?: string;
  isPregnant?: boolean;
  conditions?: string[];
  medications?: string[];
}

export const saveProfile = async (data: SaveProfilePayload): Promise<{ profile: UserProfile }> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/profile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to save profile' }));
    throw new Error(err.error || `Server responded with status: ${response.status}`);
  }
  return await response.json();
};

// ============================================
// Blood Analysis API
// ============================================

export interface BloodMarker {
  name: string;
  value: number;
  unit: string;
  referenceMin: number | null;
  referenceMax: number | null;
  status: 'normal' | 'low' | 'high';
}

export interface MarkerRemedy {
  name: string;
  description: string;
  type: string;
}

export interface MarkerRecommendation {
  markerName: string;
  status: 'low' | 'high';
  value: number;
  unit: string;
  referenceMin?: number | null;
  referenceMax?: number | null;
  explanation: string;
  remedies: MarkerRemedy[];
  dietaryAdvice: string;
}

export interface BloodAnalysisResult {
  id: string;
  markers: BloodMarker[];
  abnormalMarkers: BloodMarker[];
  recommendations: MarkerRecommendation[];
  generalAdvice: string;
  warning: string;
  testDate: string | null;
  labName: string | null;
  fileName: string;
  createdAt: string;
}

export interface BloodAnalysisListResponse {
  results: BloodAnalysisResult[];
}

export interface TrendPoint {
  date: string;
  markers: Record<string, { value: number; unit: string; status: string }>;
}

export interface BloodTrendsResponse {
  trends: TrendPoint[];
  availableMarkers: string[];
}

const getAuthToken = async (): Promise<string | null> => {
  const auth = getAuth();
  const user = auth.currentUser;
  return user ? await user.getIdToken() : null;
};

export const uploadBloodAnalysis = async (file: File): Promise<BloodAnalysisResult> => {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_URL}/blood-analysis/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `Server responded with status: ${response.status}`);
  }

  return await response.json();
};

export const getBloodAnalysisResults = async (): Promise<BloodAnalysisListResponse> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/blood-analysis/results`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  return await response.json();
};

export const getBloodAnalysisResult = async (resultId: string): Promise<BloodAnalysisResult> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/blood-analysis/results/${resultId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  return await response.json();
};

export const getBloodAnalysisTrends = async (marker?: string): Promise<BloodTrendsResponse> => {
  const headers = await getAuthHeaders();
  const params = marker ? `?marker=${encodeURIComponent(marker)}` : '';

  const response = await fetch(`${API_URL}/blood-analysis/trends${params}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  return await response.json();
};
